/**
 * IDataOperations.ts - IDataOperations - Standard interface for all data operations
 * 
 * CHANGE HISTORY:
 * - 251021_0452 [97a0397] danielbeer: fix: correct automated type annotations and test scripts
 * - 251020_0732 [95fb3a1] danielbeer: fix: complete test infrastructure - all tests passing
 * - 251020_0617 [9601b37] danielbeer: feat: implement interface-based architecture with enforcement
 */
export interface IDataOperations {
  /**
   * Create a new entity
   * 
   * Automatic middleware (when using Actr):
   * - Permission checks
   * - Logging
   * - Notifications
   * - Undo recording
   * 
   * @param entityType - Type of entity (e.g., 'task', 'idea', 'note')
   * @param data - Entity data
   * @returns Promise<T> Created entity
   * @throws Error if permission denied or creation fails
   */
  create<T = any>(entityType: string, data: Partial<T>): Promise<T>;

  /**
   * Read a single entity by ID
   * 
   * @param entityType - Type of entity
   * @param id - Entity ID
   * @returns Promise<T | null> Entity or null if not found
   */
  read<T = any>(entityType: string, id: string): Promise<T | null>;

  /**
   * Update an existing entity
   * 
   * Automatic middleware (when using Actr):
   * - Permission checks
   * - Logging
   * - Notifications (silent for updates)
   * - Undo recording (before/after state)
   * 
   * @param entityType - Type of entity
   * @param id - Entity ID
   * @param updates - Partial updates to apply
   * @returns Promise<T> Updated entity
   * @throws Error if not found, permission denied, or update fails
   */
  update<T = any>(entityType: string, id: string, updates: Partial<T>): Promise<T>;

  /**
   * Delete an entity
   * 
   * Automatic middleware (when using Actr):
   * - Permission checks
   * - Logging
   * - Notifications
   * - Undo recording (deleted entity)
   * 
   * @param entityType - Type of entity
   * @param id - Entity ID
   * @returns Promise<boolean> True if deleted, false if not found
   * @throws Error if permission denied or deletion fails
   */
  delete(entityType: string, id: string): Promise<boolean>;

  /**
   * Query entities with filters
   * 
   * @param entityType - Type of entity
   * @param filters - Query filters (optional)
   * @returns Promise<T[]> Array of matching entities
   */
  query<T = any>(entityType: string, filters?: Record<string, any>): Promise<T[]>;

  /**
   * Create a relationship between entities
   * 
   * @param entityType - Type of source entity
   * @param id - Source entity ID
   * @param relationType - Type of relationship (e.g., 'depends-on', 'related-to')
   * @param targetId - Target entity ID
   * @returns Promise<void>
   */
  relate(
    entityType: string,
    id: string,
    relationType: string,
    targetId: string
  ): Promise<void>;

  /**
   * Get related entities
   * 
   * @param entityType - Type of source entity
   * @param id - Source entity ID
   * @param relationType - Type of relationship
   * @returns Promise<string[]> Array of related entity IDs
   */
  getRelated(
    entityType: string,
    id: string,
    relationType: string
  ): Promise<string[]>;

  /**
   * Execute a custom action
   * 
   * Automatic middleware (when using Actr):
   * - Permission checks
   * - Logging
   * - Context evaluation
   * 
   * @param actionName - Name of the action
   * @param params - Action parameters
   * @returns Promise<any> Action result
   */
  execute(actionName: string, params?: Record<string, any>): Promise<any>;
}

/**
 * Type guard to check if an object implements IDataOperations
 */
export function isDataOperations(obj): obj is IDataOperations {
  return (
    obj !== null &&
    obj !== undefined &&
    typeof obj === 'object' &&
    typeof obj.create === 'function' &&
    typeof obj.read === 'function' &&
    typeof obj.update === 'function' &&
    typeof obj.delete === 'function' &&
    typeof obj.query === 'function' &&
    typeof obj.relate === 'function' &&
    typeof obj.getRelated === 'function' &&
    typeof obj.execute === 'function'
  );
}

/**
 * Assert that an object implements IDataOperations
 * Throws error if not
 */
export function assertDataOperations(obj): asserts obj is IDataOperations {
  if (!isDataOperations(obj)) {
    throw new Error(
      'Object does not implement IDataOperations interface. ' +
      'All data operations must go through Actr or a compatible implementation.'
    );
  }
}
